import { Component, OnDestroy, OnInit, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectStateService } from '../../services/project-state.service';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TaskManagementService } from '../../services/task-management.service';
import {
  TaskStatus,
  TaskStatusUpdate,
  TaskAttribute,
  TaskAttributeType,
  CreateTaskStatus,
  CreateTaskAttribute,
  TaskTag,
  CreateTaskTag,
  UpdateTaskTag
} from '../../models/task-management.models';

@Component({
  selector: 'app-task-management',
  imports: [
    CommonModule,
    FormsModule,
    ToggleSwitch,
    DialogModule,
    ConfirmDialogModule,
    ToastModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './task-management.html',
  styleUrl: './task-management.css'
})
export class TaskManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // Active tab
  activeTab: 'statuses' | 'attributes' | 'tags' = 'statuses';

  // Project context
  projectId = signal<number | null>(null);

  // Filtered arrays for search
  filteredStatuses: TaskStatus[] = [];
  filteredAttributes: TaskAttribute[] = [];
  filteredTags: TaskTag[] = [];

  // Statuses
  statuses = signal<TaskStatus[]>([]);
  statusTypes = [
    { label: 'Open', value: 'Open' },
    { label: 'Closed', value: 'Closed' },
    { label: 'Verified', value: 'Verified' }
  ];
  permissionOptions = [
    { label: 'All users', value: 'All users' },
    { label: 'Admin only', value: 'Admin only' }
  ];

  // Predefined color palette for status colors
  colorPalette: string[] = [
    '#4299E1', '#C53030', '#DD6B20', '#D69E2E', '#38A169', '#319795', '#E53E8E',
    '#90CDF4', '#FC8181', '#F6AD55', '#F6E05E', '#68D391', '#4FD1C5', '#F687B3',
    '#BEE3F8', '#FEB2B2', '#FEEBC8', '#FEFCBF', '#C6F6D5', '#B2F5EA', '#000000'
  ];

  // Track which status has color picker open
  activeColorPickerStatusId: number | null = null;
  colorPickerOpenUp = new Set<number>();
  colorPickerStyles: { [key: string]: string } = {};

  showCreateStatusDialog = signal(false);
  newStatusName = signal('');

  // Attributes
  attributes = signal<TaskAttribute[]>([]);
  attributeTypes = signal<TaskAttributeType[]>([]);
  showCreateAttributeDialog = signal(false);
  isAttributeEdit = signal(false);
  editingAttributeId = signal<number | null>(null);
  newAttributeName = signal('');
  selectedAttributeTypeId = signal<number | null>(null);
  optionText = '';
  attributeOptionValueList: string[] = [];
  existingOptions: { optionValueId?: number; valueText: string }[] = [];

  // Tags
  tags = signal<TaskTag[]>([]);
  showCreateTagDialog = signal(false);
  newTagName = signal('');

  constructor(
    private taskManagementService: TaskManagementService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private projectStateService: ProjectStateService
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const colorPaletteContainer = target.closest('.color-palette-container');
    if (!colorPaletteContainer && this.activeColorPickerStatusId !== null) {
      this.activeColorPickerStatusId = null;
      this.colorPickerOpenUp.clear();
      this.colorPickerStyles = {};
    }
  }

  ngOnInit(): void {
    const projectId = this.route.snapshot.paramMap.get('projectId');
    if (projectId) {
      this.projectId.set(parseInt(projectId, 10));
    }

    this.loadTaskStatuses();
    this.loadTaskAttributes();
    this.loadAttributeTypes();
    this.loadTaskTags();

    this.projectStateService.pageSearch$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(search => {
      this.applySearch(search);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredStatuses = [...this.statuses()];
      this.filteredAttributes = [...this.attributes()];
      this.filteredTags = [...this.tags()];
    } else {
      this.filteredStatuses = this.statuses().filter(s => (s.statusName || '').toLowerCase().includes(term));
      this.filteredAttributes = this.attributes().filter(a => (a.attributeName || '').toLowerCase().includes(term));
      this.filteredTags = this.tags().filter(t => (t.tagName || '').toLowerCase().includes(term));
    }
  }

  // Task Statuses
  loadTaskStatuses(): void {
    this.taskManagementService.getAllTaskStatuses().subscribe({
      next: (data) => {
        this.statuses.set(data);
        this.applySearch(this.projectStateService.getPageSearch());
      },
      error: (error) => {
        console.error('Error loading task statuses:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load task statuses' });
      }
    });
  }

  openCreateStatusDialog(): void {
    this.newStatusName.set('');
    this.showCreateStatusDialog.set(true);
  }

  createStatus(): void {
    if (!this.newStatusName().trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Status name is required' });
      return;
    }

    const model: CreateTaskStatus = {
      statusName: this.newStatusName()
    };

    this.taskManagementService.createTaskStatus(model).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Status created successfully' });
        this.showCreateStatusDialog.set(false);
        this.loadTaskStatuses();
      },
      error: (error) => {
        console.error('Error creating status:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create status' });
      }
    });
  }

  updateStatus(status: TaskStatus): void {
    const updateModel: TaskStatusUpdate = {
      taskId: 0, // This method signature seems wrong - we need a taskId parameter
      statusId: status.statusId
    };
    this.taskManagementService.updateTaskStatusDefinition({
      statusId: status.statusId,
      statusName: status.statusName,
      color: status.color,
      statusType: status.statusType,
      permissions: status.permissions,
      sortOrder: status.sortOrder
    }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Status updated successfully' });
      },
      error: (error) => {
        console.error('Error updating status:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status' });
        this.loadTaskStatuses();
      }
    });
  }

  deleteStatus(status: TaskStatus): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${status.statusName}"?`,
      accept: () => {
        this.taskManagementService.deleteTaskStatus(status.statusId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Status deleted successfully' });
            this.loadTaskStatuses();
          },
          error: (error) => {
            console.error('Error deleting status:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete status' });
          }
        });
      }
    });
  }

  moveStatusUp(status: TaskStatus, index: number): void {
    if (index === 0) return;
    const statusList = this.statuses();
    [statusList[index - 1], statusList[index]] = [statusList[index], statusList[index - 1]];
    statusList.forEach((s, i) => s.sortOrder = i + 1);
    this.statuses.set([...statusList]);
    this.applySearch(this.projectStateService.getPageSearch());
    this.reorderStatuses();
  }

  moveStatusDown(status: TaskStatus, index: number): void {
    const statusList = this.statuses();
    if (index === statusList.length - 1) return;
    [statusList[index], statusList[index + 1]] = [statusList[index + 1], statusList[index]];
    statusList.forEach((s, i) => s.sortOrder = i + 1);
    this.statuses.set([...statusList]);
    this.applySearch(this.projectStateService.getPageSearch());
    this.reorderStatuses();
  }

  reorderStatuses(): void {
    const statusIds = this.statuses().map(s => s.statusId);
    this.taskManagementService.reorderTaskStatuses(statusIds).subscribe({
      error: (error) => {
        console.error('Error reordering statuses:', error);
        this.loadTaskStatuses();
      }
    });
  }

  toggleColorPicker(statusId: number, event?: MouseEvent): void {
    if (this.activeColorPickerStatusId === statusId) {
      this.activeColorPickerStatusId = null;
      this.colorPickerOpenUp.clear();
      this.colorPickerStyles = {};
    } else {
      this.activeColorPickerStatusId = statusId;
      this.colorPickerOpenUp.clear();
      if (event && event.target) {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 220;
        const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        if (shouldOpenUp) {
          this.colorPickerOpenUp.add(statusId);
        }
        const top = shouldOpenUp
          ? Math.max(8, rect.top - dropdownHeight - 4)
          : Math.min(window.innerHeight - dropdownHeight - 8, rect.bottom + 4);
        const left = rect.left + rect.width / 2;
        this.colorPickerStyles = {
          top: `${top}px`,
          left: `${left}px`,
          transform: 'translateX(-50%)'
        };
      }
    }
  }

  selectColor(status: TaskStatus, color: string): void {
    status.color = color;
    this.activeColorPickerStatusId = null;
    this.updateStatus(status);
  }

  closeColorPicker(): void {
    this.activeColorPickerStatusId = null;
  }

  // Task Attributes
  loadTaskAttributes(): void {
    this.taskManagementService.getAllTaskAttributes().subscribe({
      next: (data) => {
        this.attributes.set(data);
        this.applySearch(this.projectStateService.getPageSearch());
      },
      error: (error) => {
        console.error('Error loading task attributes:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load task attributes' });
      }
    });
  }

  loadAttributeTypes(): void {
    this.taskManagementService.getAllTaskAttributeTypes().subscribe({
      next: (data) => {
        this.attributeTypes.set(data);
      },
      error: (error) => {
        console.error('Error loading attribute types:', error);
      }
    });
  }

  openCreateAttributeDialog(): void {
    this.isAttributeEdit.set(false);
    this.editingAttributeId.set(null);
    this.newAttributeName.set('');
    this.selectedAttributeTypeId.set(null);
    this.optionText = '';
    this.attributeOptionValueList = [];
    this.existingOptions = [];
    this.showCreateAttributeDialog.set(true);
  }

  openEditAttributeDialog(attribute: TaskAttribute): void {
    this.isAttributeEdit.set(true);
    this.editingAttributeId.set(attribute.attributeId);
    this.newAttributeName.set(attribute.attributeName);
    this.selectedAttributeTypeId.set(attribute.attributeTypeId);
    this.optionText = '';
    this.attributeOptionValueList = [];
    this.existingOptions = [];

    // Load existing options for option-list attributes.
    if (this.isOptionListTypeName(attribute.attributeTypeName)) {
      this.loadAttributeOptions(attribute.attributeId);
    }

    this.showCreateAttributeDialog.set(true);
  }

  loadAttributeOptions(attributeId: number): void {
    this.taskManagementService.getTaskAttributeOptions(attributeId).subscribe({
      next: (options) => {
        this.existingOptions = options.map(opt => ({
          optionValueId: opt.optionValueId,
          valueText: opt.valueText
        }));
      },
      error: (error) => {
        console.error('Error loading attribute options:', error);
      }
    });
  }

  createAttribute(): void {
    if (this.isAttributeEdit()) {
      this.updateAttribute();
      return;
    }

    if (!this.newAttributeName().trim() || !this.selectedAttributeTypeId()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'All fields are required' });
      return;
    }

    const selectedType = this.attributeTypes().find(t => t.attributeTypeId === this.selectedAttributeTypeId());
    const hasOptions = this.isOptionListTypeName(selectedType?.typeName);

    if (hasOptions) {
      if (this.attributeOptionValueList.length === 0) {
        this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please add at least one option' });
        return;
      }
    }

    const model: CreateTaskAttribute = {
      attributeName: this.newAttributeName(),
      attributeTypeId: this.selectedAttributeTypeId()!,
      optionValues: hasOptions ? this.attributeOptionValueList : undefined
    };

    this.taskManagementService.createTaskAttribute(model).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attribute created successfully' });
        this.showCreateAttributeDialog.set(false);
        this.loadTaskAttributes();
      },
      error: (error) => {
        console.error('Error creating attribute:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create attribute' });
      }
    });
  }

  updateAttribute(): void {
    if (!this.newAttributeName().trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Attribute name is required' });
      return;
    }

    const selectedType = this.attributeTypes().find(t => t.attributeTypeId === this.selectedAttributeTypeId());
    const hasOptions = this.isOptionListTypeName(selectedType?.typeName);

    // Validate option-list attributes have options (existing + new).
    if (hasOptions && this.existingOptions.length === 0 && this.attributeOptionValueList.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please add at least one option' });
      return;
    }

    // Combine existing (edited) options and new options
    const allOptions = [
      ...this.existingOptions.map(opt => opt.valueText),
      ...this.attributeOptionValueList
    ];

    this.taskManagementService.updateTaskAttributeFull({
      attributeId: this.editingAttributeId()!,
      attributeName: this.newAttributeName(),
      optionValues: hasOptions ? allOptions : undefined
    }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attribute updated successfully' });
        this.showCreateAttributeDialog.set(false);
        this.loadTaskAttributes();
      },
      error: (error) => {
        console.error('Error updating attribute:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update attribute' });
      }
    });
  }

  addOptionText(): void {
    if (!this.optionText.trim()) {
      return;
    }

    // Check for duplicates in both existing and new options
    const allOptionTexts = [
      ...this.existingOptions.map(opt => opt.valueText),
      ...this.attributeOptionValueList
    ];

    if (allOptionTexts.includes(this.optionText.trim())) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Option already exists' });
      return;
    }

    this.attributeOptionValueList.push(this.optionText.trim());
    this.optionText = '';
  }

  removeOptionText(index: number): void {
    this.attributeOptionValueList.splice(index, 1);
  }

  updateExistingOption(index: number, newValue: string): void {
    if (!newValue.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Option value cannot be empty' });
      return;
    }

    // Check for duplicates (excluding current option)
    const allOtherOptions = [
      ...this.existingOptions.filter((_, i) => i !== index).map(opt => opt.valueText),
      ...this.attributeOptionValueList
    ];

    if (allOtherOptions.includes(newValue.trim())) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Option already exists' });
      return;
    }

    this.existingOptions[index].valueText = newValue.trim();
  }

  removeExistingOption(index: number): void {
    this.existingOptions.splice(index, 1);
  }

  isDropdownType(): boolean {
    const selectedType = this.attributeTypes().find(t => t.attributeTypeId === this.selectedAttributeTypeId());
    return this.isOptionListTypeName(selectedType?.typeName);
  }

  private isOptionListTypeName(typeName?: string | null): boolean {
    return typeName === 'Dropdown' || typeName === 'Multiselect';
  }

  toggleAttributeDisplay(attribute: TaskAttribute): void {
    this.taskManagementService.updateTaskAttribute({
      attributeId: attribute.attributeId,
      isDisplayed: attribute.isDisplayed,
      sortOrder: attribute.sortOrder
    }).subscribe({
      error: (error) => {
        console.error('Error updating attribute:', error);
        attribute.isDisplayed = !attribute.isDisplayed;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update attribute' });
      }
    });
  }

  moveAttributeUp(attribute: TaskAttribute, index: number): void {
    if (index === 0) return;
    const attrList = this.attributes();
    [attrList[index - 1], attrList[index]] = [attrList[index], attrList[index - 1]];
    attrList.forEach((a, i) => a.sortOrder = i + 1);
    this.attributes.set([...attrList]);
    this.applySearch(this.projectStateService.getPageSearch());
    this.reorderAttributes();
  }

  moveAttributeDown(attribute: TaskAttribute, index: number): void {
    const attrList = this.attributes();
    if (index === attrList.length - 1) return;
    [attrList[index], attrList[index + 1]] = [attrList[index + 1], attrList[index]];
    attrList.forEach((a, i) => a.sortOrder = i + 1);
    this.attributes.set([...attrList]);
    this.applySearch(this.projectStateService.getPageSearch());
    this.reorderAttributes();
  }

  reorderAttributes(): void {
    const attributeIds = this.attributes().map(a => a.attributeId);
    this.taskManagementService.reorderTaskAttributes(attributeIds).subscribe({
      error: (error) => {
        console.error('Error reordering attributes:', error);
        this.loadTaskAttributes();
      }
    });
  }

  deleteAttribute(attribute: TaskAttribute): void {
    if (attribute.isSystem) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Cannot delete system attributes' });
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${attribute.attributeName}"?`,
      accept: () => {
        this.taskManagementService.deleteTaskAttribute(attribute.attributeId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attribute deleted successfully' });
            this.loadTaskAttributes();
          },
          error: (error) => {
            console.error('Error deleting attribute:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete attribute' });
          }
        });
      }
    });
  }

  getAttributeIconRi(typeName: string): string {
    const iconMap: { [key: string]: string } = {
      'Status': 'checkbox-circle-line',
      'Dropdown': 'list-check',
      'User': 'user-line',
      'Date': 'calendar-line',
      'Text': 'text',
      'Number': 'hashtag'
    };
    return iconMap[typeName] || 'question-line';
  }

  // Task Tags
  loadTaskTags(): void {
    this.taskManagementService.getAllTaskTags().subscribe({
      next: (data) => {
        this.tags.set(data);
        this.applySearch(this.projectStateService.getPageSearch());
      },
      error: (error) => {
        console.error('Error loading task tags:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load task tags' });
      }
    });
  }

  openCreateTagDialog(): void {
    this.newTagName.set('');
    this.showCreateTagDialog.set(true);
  }

  createTag(): void {
    if (!this.newTagName().trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Tag name is required' });
      return;
    }

    const model: CreateTaskTag = {
      tagName: this.newTagName()
    };

    this.taskManagementService.createTaskTag(model).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag created successfully' });
        this.showCreateTagDialog.set(false);
        this.loadTaskTags();
      },
      error: (error) => {
        console.error('Error creating tag:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create tag' });
      }
    });
  }

  updateTag(tag: TaskTag): void {
    const model: UpdateTaskTag = {
      tagId: tag.tagId,
      tagName: tag.tagName
    };

    this.taskManagementService.updateTaskTag(model).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag updated successfully' });
      },
      error: (error) => {
        console.error('Error updating tag:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update tag' });
        this.loadTaskTags();
      }
    });
  }

  deleteTag(tag: TaskTag): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${tag.tagName}"?`,
      accept: () => {
        this.taskManagementService.deleteTaskTag(tag.tagId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag deleted successfully' });
            this.loadTaskTags();
          },
          error: (error) => {
            console.error('Error deleting tag:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete tag' });
          }
        });
      }
    });
  }
}
